import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  CircularProgress,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSpace } from "../../contexts/SpaceContext";
import SpaceApps from "../../pages/dashboards/Default/index";

// Styled components
const Alert = styled(MuiAlert)(spacing);

function ProtectedPage() {
  // Hooks from both components
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    spaces,
    currentSpace,
    loading,
    initialized,
    changeSpace,
    refreshSpaces,
  } = useSpace();

  // Local state for component loading
  const [isInitialized, setIsInitialized] = useState(false);
  const [localLoading, setLocalLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // Debug logs to help diagnose the issue
  useEffect(() => {
    console.log("ProtectedPage rendered with:", {
      spaces: spaces?.length || 0,
      currentSpace: currentSpace?.id || "none",
      contextLoading: loading,
      contextInitialized: initialized,
      componentInitialized: isInitialized,
      localLoading,
      retryCount,
    });
  }, [
    spaces,
    currentSpace,
    loading,
    isInitialized,
    localLoading,
    retryCount,
    initialized,
  ]);

  // Handle space initialization logic
  useEffect(() => {
    // Skip this effect until Space context has finished its initial loading
    if (loading || !initialized) {
      return;
    }

    // Set local loading state
    setLocalLoading(true);

    // If we have spaces but no current space, try to select one
    if (spaces?.length > 0 && !currentSpace) {
      // If URL has a space ID, try to select that space
      if (id) {
        console.log("Changing to space from URL:", id);
        changeSpace(id);
        setLocalLoading(false);
        setIsInitialized(true);
        return;
      }

      // If no URL ID but spaces exist, select the first one
      console.log("No space in URL, selecting first available space");
      changeSpace(spaces[0].id);
      navigate(`/spaces/${spaces[0].id}`, { replace: true });
      setLocalLoading(false);
      setIsInitialized(true);
      return;
    }

    // If we have a current space but the URL doesn't match
    if (currentSpace && (!id || currentSpace.id !== id)) {
      console.log("Current space doesn't match URL, redirecting");
      navigate(`/spaces/${currentSpace.id}`, { replace: true });
      setLocalLoading(false);
      setIsInitialized(true);
      return;
    }

    // If we have a current space that matches the URL
    if (currentSpace && id === currentSpace.id) {
      console.log("Space matches URL, ready to render");
      setLocalLoading(false);
      setIsInitialized(true);
      return;
    }

    // If we have no spaces and we're not loading
    if (!loading && spaces?.length === 0 && retryCount < 2) {
      console.log("No spaces available, retrying...");
      setRetryCount((prev) => prev + 1);
      refreshSpaces(); // Try refreshing spaces
      return;
    }

    // If we've retried and still no spaces
    if (!loading && spaces?.length === 0 && retryCount >= 2) {
      console.log("No spaces available after retries");
      setLocalLoading(false);
      setIsInitialized(true);
    }
  }, [
    spaces,
    currentSpace,
    loading,
    id,
    navigate,
    changeSpace,
    retryCount,
    refreshSpaces,
    initialized,
  ]);

  // Handle retry
  const handleRetry = () => {
    setLocalLoading(true);
    setRetryCount(0);
    refreshSpaces();
  };

  // Show loading indicator while initialization is in progress
  if (loading || localLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading your workspace...
        </Typography>
      </Box>
    );
  }

  // If we have no spaces after initialization
  if (isInitialized && !currentSpace && spaces?.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          No workspace available
        </Typography>
        <Typography variant="body1" paragraph>
          You don't have access to any workspace. Please contact your
          administrator.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  // If spaces are available and a current space is selected
  return (
    <React.Fragment>
      {location.state?.message && (
        <>
          <Alert severity="success" color="warning">
            {location.state.message}
          </Alert>
          <br />
        </>
      )}
      <SpaceApps />
    </React.Fragment>
  );
}

export default ProtectedPage;
